import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';

/**
 * Supported format patterns for legfiDate pipe.
 *
 * Angular-style presets:
 *   - medium, short, fullDate, longDate, mediumDate, shortDate, mediumTime, shortTime
 *
 * Legacy Moment-style patterns (mapped to Luxon equivalents):
 *   - MM/DD/YYYY, MM/DD, M/D/Y, MMM D, MMM D YYYY, MMMM D YYYY
 *   - MM/DD/YYYY h:mm a, MM/DD/YYYY h:mm:ss a, MMM D hh:mm A
 *   - MM/DD/YYYY at h:mm a, MM/DD/YYYY at h:mm:ss a
 *   - MMM Do YYYY (ordinal day)
 */
type DatePattern =
    // Angular-style presets
    | 'medium'
    | 'short'
    | 'fullDate'
    | 'longDate'
    | 'mediumDate'
    | 'shortDate'
    | 'mediumTime'
    | 'shortTime'
    // Legacy Moment patterns - dates only
    | 'MM/DD/YYYY'
    | 'MM/DD'
    | 'M/D/Y'
    | 'MMM D'
    | 'MMM D, YYYY'
    | 'MMMM D, YYYY'
    | 'MMM Do, YYYY'
    // Legacy Moment patterns - date + time
    | 'MM/DD/YYYY h:mm a'
    | 'MM/DD/YYYY hh:mm a'
    | 'MM/DD/YYYY h:mm:ss a'
    | 'MMM D, hh:mm A'
    // Legacy Moment patterns - date + time with "at"
    | 'MM/DD/YYYY [at] h:mm a'
    | 'MM/DD/YYYY [at] h:mm:ss a';

@Pipe({
    name: 'legfiDate',
    standalone: true,
})
@Injectable({
    providedIn: 'root',
})
export class LegfiDatePipe implements PipeTransform
{
    public transform(value: any, pattern: DatePattern = 'mediumDate') {
        const timezone = LegFiJwtService.getTimezone();

        // If already a moment object, use moment formatting (legacy path)
        if (moment.isMoment(value)) {
            return this.formatWithMoment(value, pattern);
        }

        // For bare values (string/number), use luxon
        if (typeof value === 'string') {
            let dt: DateTime;
            // if we didnt pass a time, use the date as-is in the target timezone
            if (value.indexOf(':') === -1) {
                dt = DateTime.fromISO(value, { zone: timezone });
            } else {
                dt = DateTime.fromISO(value, { zone: 'utc' });
                if (!dt.isValid) {
                    // Handle SQL datetime format: 'YYYY-MM-DD HH:mm:ss'
                    dt = DateTime.fromSQL(value, { zone: 'utc' });
                }
                if (!dt.isValid) {
                    // Return original value if both parsing attempts fail
                    return value;
                }
                dt = dt.setZone(timezone);
            }
            return this.formatWithLuxon(dt, pattern);
        }

        if (typeof value === 'number' && value > 0) {
            // Detect precision by magnitude: timestamps > 10 billion are milliseconds,
            // smaller values are seconds. This threshold safely distinguishes because:
            // - Unix seconds for year 2033 ≈ 2 billion (10 digits)
            // - Unix milliseconds for year 2001 ≈ 1 trillion (13 digits)
            const MS_THRESHOLD = 10_000_000_000;
            let dt: DateTime;
            if (value > MS_THRESHOLD) {
                dt = DateTime.fromMillis(value, { zone: 'utc' }).setZone(timezone);
            } else {
                dt = DateTime.fromSeconds(value, { zone: 'utc' }).setZone(timezone);
            }
            return this.formatWithLuxon(dt, pattern);
        }

        return value;
    }

    private formatWithMoment(value: moment.Moment, pattern: DatePattern): string {
        switch (pattern) {
            // Angular-style presets
            case 'medium':
                return value.format('MMM D, YYYY, hh:mm:ss A');
            case 'short':
                return value.format('M/D/YYYY, hh:mm A');
            case 'fullDate':
                return value.format('dddd, MMMM D, YYYY');
            case 'longDate':
                return value.format('MMMM D, YYYY');
            case 'mediumDate':
                return value.format('MMM D, YYYY');
            case 'shortDate':
                return value.format('M/D/YYYY');
            case 'mediumTime':
                return value.format('hh:mm:ss A');
            case 'shortTime':
                return value.format('hh:mm A');
            // Legacy Moment patterns - pass through exactly
            case 'MM/DD/YYYY':
            case 'MM/DD':
            case 'M/D/Y':
            case 'MMM D':
            case 'MMM D, YYYY':
            case 'MMMM D, YYYY':
            case 'MMM Do, YYYY':
            case 'MM/DD/YYYY h:mm a':
            case 'MM/DD/YYYY hh:mm a':
            case 'MM/DD/YYYY h:mm:ss a':
            case 'MMM D, hh:mm A':
            case 'MM/DD/YYYY [at] h:mm a':
            case 'MM/DD/YYYY [at] h:mm:ss a':
                return value.format(pattern);
        }
    }

    private formatWithLuxon(value: DateTime, pattern: DatePattern): string {
        if (!value.isValid) {
            return '';
        }
        switch (pattern) {
            // Angular-style presets (use uppercase AM/PM to match Angular DatePipe)
            case 'medium':
                return this.formatWithUppercaseMeridiem(value, 'MMM d, yyyy, hh:mm:ss a');
            case 'short':
                return this.formatWithUppercaseMeridiem(value, 'M/d/yyyy, hh:mm a');
            case 'fullDate':
                return value.toFormat('EEEE, MMMM d, yyyy');
            case 'longDate':
                return value.toFormat('MMMM d, yyyy');
            case 'mediumDate':
                return value.toFormat('MMM d, yyyy');
            case 'shortDate':
                return value.toFormat('M/d/yyyy');
            case 'mediumTime':
                return this.formatWithUppercaseMeridiem(value, 'hh:mm:ss a');
            case 'shortTime':
                return this.formatWithUppercaseMeridiem(value, 'hh:mm a');
            // Legacy Moment patterns - dates only
            // MM = zero-padded month, DD = zero-padded day in Moment → MM, dd in Luxon
            case 'MM/DD/YYYY':
                return value.toFormat('MM/dd/yyyy');
            case 'MM/DD':
                return value.toFormat('MM/dd');
            case 'M/D/Y':
                return value.toFormat('M/d/yyyy');
            case 'MMM D':
                return value.toFormat('MMM d');
            case 'MMM D, YYYY':
                return value.toFormat('MMM d, yyyy');
            case 'MMMM D, YYYY':
                return value.toFormat('MMMM d, yyyy');
            case 'MMM Do, YYYY':
                return this.formatOrdinalDay(value, 'MMM {o}, yyyy');
            // Legacy Moment patterns - date + time
            // Moment 'a' = lowercase am/pm, 'A' = uppercase AM/PM
            case 'MM/DD/YYYY h:mm a':
                return this.formatWithLowercaseMeridiem(value, 'MM/dd/yyyy h:mm a');
            case 'MM/DD/YYYY hh:mm a':
                return this.formatWithLowercaseMeridiem(value, 'MM/dd/yyyy hh:mm a');
            case 'MM/DD/YYYY h:mm:ss a':
                return this.formatWithLowercaseMeridiem(value, 'MM/dd/yyyy h:mm:ss a');
            case 'MMM D, hh:mm A':
                return this.formatWithUppercaseMeridiem(value, 'MMM d, hh:mm a');
            // Legacy Moment patterns - date + time with "at"
            case 'MM/DD/YYYY [at] h:mm a':
                return this.formatWithLowercaseMeridiem(value, 'MM/dd/yyyy \'at\' h:mm a');
            case 'MM/DD/YYYY [at] h:mm:ss a':
                return this.formatWithLowercaseMeridiem(value, 'MM/dd/yyyy \'at\' h:mm:ss a');
        }
    }

    /**
     * Luxon's 'a' token is locale-dependent and may produce "am/pm" or "AM/PM".
     * Moment's 'A' token always produces uppercase "AM/PM".
     * This method ensures consistent uppercase meridiem output to match Moment's 'A' behavior.
     */
    private formatWithUppercaseMeridiem(value: DateTime, pattern: string): string {
        const formatted = value.toFormat(pattern);
        return formatted.replace(/\bam\b/gi, 'AM').replace(/\bpm\b/gi, 'PM');
    }

    /**
     * Moment's 'a' token always produces lowercase "am/pm".
     * This method ensures consistent lowercase meridiem output to match Moment's 'a' behavior.
     */
    private formatWithLowercaseMeridiem(value: DateTime, pattern: string): string {
        const formatted = value.toFormat(pattern);
        return formatted.replace(/\bAM\b/gi, 'am').replace(/\bPM\b/gi, 'pm');
    }

    /**
     * Luxon doesn't support ordinal day formatting (1st, 2nd, 3rd, etc.).
     * This method handles patterns with {o} placeholder for ordinal day.
     */
    private formatOrdinalDay(value: DateTime, pattern: string): string {
        const day = value.day;
        const ordinal = this.getOrdinalSuffix(day);
        const formatted = value.toFormat(pattern.replace('{o}', `d'${ordinal}'`));
        return formatted;
    }

    private getOrdinalSuffix(n: number): string {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
    }
}
